// Shared URL contract for the cryod drill-down detail page (/ci/overview/g).
// The slice a detail page shows is encoded as repeated `filter=dim=value` params
// plus the carried metric/agg/window. Both the overview row-click and the detail
// page's nested re-pivot build hrefs through here so drilling composes cleanly.

export interface CryodDetailPin {
  dimension: string;
  value: string;
}

export interface CryodViewParams {
  metric: string;
  agg: string;
  window: string;
}

export const CRYOD_DETAIL_PATH = "/ci/overview/g";

export function buildCryodDetailHref(options: {
  filters: string[]; // existing "dim=value" base filters
  pins: CryodDetailPin[]; // new pins to append (the clicked slice)
  view: CryodViewParams;
}): string {
  const query = new URLSearchParams();
  for (const filter of options.filters) query.append("filter", filter);
  for (const pin of options.pins) query.append("filter", `${pin.dimension}=${pin.value}`);
  query.set("metric", options.view.metric);
  query.set("agg", options.view.agg);
  query.set("window", options.view.window);
  return `${CRYOD_DETAIL_PATH}?${query.toString()}`;
}

// Parse the repeated `filter=dim=value` params into pins (first "=" splits).
// This is the detail page's *ordered list* encoding (lets the breadcrumb carry an
// ordered, repeatable drill chain).
export function parseCryodFilters(searchParams: URLSearchParams): CryodDetailPin[] {
  const pins: CryodDetailPin[] = [];
  for (const raw of searchParams.getAll("filter")) {
    const index = raw.indexOf("=");
    if (index > 0) {
      pins.push({ dimension: raw.slice(0, index), value: raw.slice(index + 1) });
    }
  }
  return pins;
}

// The overview page uses a *map* encoding instead — one `f_<dim>=<value>` param
// per dimension — because its filter dropdowns are single-select-per-dimension
// (last-write-wins is natural for a URLSearchParams key). Both encodings parse to
// the same pin shape; they live here together so the contract has one home.
export const OVERVIEW_FILTER_PREFIX = "f_";

export function parseOverviewFilters(searchParams: URLSearchParams): CryodDetailPin[] {
  const pins: CryodDetailPin[] = [];
  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith(OVERVIEW_FILTER_PREFIX) && value) {
      pins.push({ dimension: key.slice(OVERVIEW_FILTER_PREFIX.length), value });
    }
  }
  return pins;
}

// Serialize pins to the API wire form (`dim=value`), shared by both pages.
export function pinsToFilterParams(pins: CryodDetailPin[]): string[] {
  return pins.map((pin) => `${pin.dimension}=${pin.value}`);
}
